import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getPageLinkByNodeId,
    LinkModel,
    mapLinks,
    useGetLink,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProjectTileCard, { ProjectTileStyles } from "@ucs/Components/Project/ProjectTileCard";
import * as React from "react";
import { connect } from "react-redux";

const enum fields {
    image = "image",
    smallImage = "smallImage",
    projectLink = "projectLink",
    categoryName = "categoryName",
    projectName = "projectName",
    title = "title",
    productLinks = "productLinks",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.smallImage]: string;
        [fields.projectLink]: LinkFieldValue;
        [fields.categoryName]: string;
        [fields.projectName]: string;
        [fields.productLinks]: LinkModel[];
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const links = mapLinks<LinkModel, { openInNewWindow: boolean; overriddenTitle: string }>(
        state,
        ownProps.fields.productLinks,
        widgetLink => ({
            openInNewWindow: widgetLink.fields.openInNewWindow,
            overriddenTitle: widgetLink.fields.overriddenTitle,
        }),
    );
    return {
        links,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const projectTileStyles: ProjectTileStyles = {};

const styles = projectTileStyles;

const ProjectTile: React.FC<Props> = ({ fields, links }) => {
    const projectLink = useGetLink(fields.projectLink);
    const { image, smallImage, projectName, categoryName } = fields;
    const mappedLinks = links.map(l => ({
        url: l.url,
        title: l.title,
        openInNewWindow: l.openInNewWindow ?? false,
        overriddenTitle: l.overriddenTitle ?? "",
    }));
    return (
        <ProjectTileCard
            image={smallImage || image}
            projectLink={projectLink.url || ""}
            categoryName={categoryName}
            projectName={projectName}
            productLinks={mappedLinks}
            extendedStyles={styles}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProjectTile),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.smallImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.projectLink,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.categoryName,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.projectName,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.productLinks,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
